import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Hammer, ChevronDown, ChevronUp } from "lucide-react";

/**
 * List Builder — create a new fundraising list (category) from AND-combined
 * filter criteria over the client's canonical donors. STATIC SNAPSHOT:
 * memberships are added once at creation ($addToSet server-side); donors
 * are never copied or moved and stay on their original lists.
 */
export const ListBuilderPanel = ({ lists = [], onCreated = null }) => {
  const [open, setOpen] = useState(false);
  const [minGiven, setMinGiven] = useState("");
  const [maxGiven, setMaxGiven] = useState("");
  const [givingField, setGivingField] = useState("total_given");
  const [cities, setCities] = useState("");
  const [state, setState] = useState("");
  const [onIds, setOnIds] = useState([]);
  const [notOnIds, setNotOnIds] = useState([]);
  const [maxed, setMaxed] = useState("any");
  const [name, setName] = useState("");
  const [type, setType] = useState("call_list");
  const [preview, setPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const timer = useRef(null);

  const filters = useMemo(() => ({
    min_total_given: minGiven === "" ? null : Number(minGiven),
    max_total_given: maxGiven === "" ? null : Number(maxGiven),
    giving_field: givingField,
    cities: cities.split(",").map((c) => c.trim()).filter(Boolean),
    state: state.trim() || null,
    on_list_ids: onIds,
    not_on_list_ids: notOnIds,
    maxed,
  }), [minGiven, maxGiven, givingField, cities, state, onIds, notOnIds, maxed]);

  // Live count preview — debounced as criteria change.
  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/caller/list-builder/preview", { filters });
        setPreview(data);
      } catch {
        setPreview(null);
      }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [open, filters]);

  const toggleId = (id, group) => {
    const [vals, setVals, other, setOther] = group === "on"
      ? [onIds, setOnIds, notOnIds, setNotOnIds]
      : [notOnIds, setNotOnIds, onIds, setOnIds];
    if (vals.includes(id)) setVals(vals.filter((x) => x !== id));
    else {
      setVals([...vals, id]);
      // A list can't be required AND excluded at once.
      if (other.includes(id)) setOther(other.filter((x) => x !== id));
    }
  };

  const create = async () => {
    if (creating || !name.trim() || !preview || preview.count === 0) return;
    setCreating(true);
    try {
      const { data } = await api.post("/caller/list-builder/create", {
        name: name.trim(), category_type: type, filters,
      });
      toast.success(`"${data.category.name}" created with ${data.member_count.toLocaleString()} donors`);
      setName("");
      setOpen(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const chip = (l, group, active) => (
    <button
      key={`${group}-${l.id}`}
      type="button"
      onClick={() => toggleId(l.id, group)}
      data-testid={`list-builder-${group}-${l.id}`}
      className={`text-[11px] font-bold px-2 py-1 rounded-md border transition-colors ${
        active
          ? "bg-flag-navy text-white border-flag-navy"
          : "bg-white text-slate-600 border-slate-300 hover:border-flag-navy"
      }`}
    >
      {l.name}
    </button>
  );

  return (
    <div className="mt-3 border-2 border-flag-navy/20 bg-flag-navy/[0.03] rounded-xl" data-testid="list-builder-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 p-3 text-left"
        data-testid="list-builder-toggle"
      >
        <div className="flex items-center gap-2">
          <Hammer className="h-4 w-4 text-flag-navy shrink-0" />
          <div>
            <span className="font-display font-black text-flag-navy text-sm">Build a list</span>
            <span className="ml-2 text-[11px] text-slate-500">filter your donors into a new call or prospecting list</span>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <label className="text-[11px] font-bold text-slate-600">
              Filter by
              <select
                value={givingField}
                onChange={(e) => setGivingField(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 px-2 text-sm font-normal bg-white"
                data-testid="list-builder-giving-field"
              >
                <option value="total_given">Total given (lifetime)</option>
                <option value="highest_gift">Highest gift (biggest single check)</option>
              </select>
            </label>
            <label className="text-[11px] font-bold text-slate-600">
              {givingField === "highest_gift" ? "Highest gift" : "Total given"} ≥ $
              <input type="number" min="0" value={minGiven} onChange={(e) => setMinGiven(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 px-2 text-sm font-normal"
                placeholder="500" data-testid="list-builder-min-given" />
            </label>
            <label className="text-[11px] font-bold text-slate-600">
              {givingField === "highest_gift" ? "Highest gift" : "Total given"} ≤ $
              <input type="number" min="0" value={maxGiven} onChange={(e) => setMaxGiven(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 px-2 text-sm font-normal"
                placeholder="no limit" data-testid="list-builder-max-given" />
            </label>
            <label className="text-[11px] font-bold text-slate-600">
              City / cities
              <input value={cities} onChange={(e) => setCities(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 px-2 text-sm font-normal"
                placeholder="Bend, Salem" data-testid="list-builder-cities" />
            </label>
            <label className="text-[11px] font-bold text-slate-600">
              State
              <input value={state} onChange={(e) => setState(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 px-2 text-sm font-normal"
                placeholder="OR" data-testid="list-builder-state" />
            </label>
          </div>

          {lists.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <div className="text-[11px] font-bold text-slate-600 mb-1">Must be ON list</div>
                <div className="flex flex-wrap gap-1.5">{lists.map((l) => chip(l, "on", onIds.includes(l.id)))}</div>
              </div>
              <div>
                <div className="text-[11px] font-bold text-slate-600 mb-1">Must NOT be on list</div>
                <div className="flex flex-wrap gap-1.5">{lists.map((l) => chip(l, "noton", notOnIds.includes(l.id)))}</div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[11px] font-bold text-slate-600">Maxed donors ($7,000+):</span>
            {[["any", "Include"], ["exclude", "Exclude"], ["only", "Only maxed"]].map(([v, label]) => (
              <label key={v} className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" name="lb-maxed" checked={maxed === v} onChange={() => setMaxed(v)}
                  className="accent-flag-red" data-testid={`list-builder-maxed-${v}`} />
                {label}
              </label>
            ))}
          </div>

          <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm" data-testid="list-builder-count">
            {preview
              ? <><strong className="text-flag-navy">{preview.count.toLocaleString()}</strong> of {preview.total_donors.toLocaleString()} donors match these filters</>
              : "Counting…"}
          </div>

          <div className="flex items-end gap-2 flex-wrap">
            <label className="text-[11px] font-bold text-slate-600 grow min-w-[160px]">
              New list name
              <input value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-slate-300 px-2 text-sm font-normal"
                placeholder="Bend $500+ prospects" data-testid="list-builder-name" />
            </label>
            <label className="text-[11px] font-bold text-slate-600">
              Type
              <select value={type} onChange={(e) => setType(e.target.value)}
                className="mt-1 block h-10 rounded-md border border-slate-300 px-2 text-sm font-normal bg-white"
                data-testid="list-builder-type">
                <option value="call_list">Call List</option>
                <option value="pac_list">PAC List (no cap)</option>
                <option value="prospecting_list">Prospecting List</option>
              </select>
            </label>
            <button
              type="button"
              onClick={create}
              disabled={creating || !name.trim() || !preview || preview.count === 0}
              className="h-10 px-4 rounded-md bg-flag-red text-white font-bold text-sm hover:opacity-90 disabled:opacity-40"
              data-testid="list-builder-create-btn"
            >
              {creating ? "Creating…" : "Create list"}
            </button>
          </div>
          <div className="text-[10px] text-slate-400">
            Static snapshot: the list is built once from donors matching right now — donors gain a membership, records are never copied, and everyone stays on their existing lists.
          </div>
        </div>
      )}
    </div>
  );
};
